import React, { useState, useEffect } from "react";
import { Button, Select, notification, Input, Modal, Form, Upload, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import ImgCrop from "antd-img-crop";

import axiosInstance from "utilities/axios";
import MSRPModal from "./MSRPModal";

const { Text } = Typography;

const isValidURL = (url) => {
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

var __awaiter =
  (this && this.__awaiter) ||
  function (thisArg, _arguments, P, generator) {
    function adopt(value) {
      return value instanceof P
        ? value
        : new P(function (resolve) {
            resolve(value);
          });
    }
    return new (P || (P = Promise))(function (resolve, reject) {
      function fulfilled(value) {
        try {
          step(generator.next(value));
        } catch (e) {
          reject(e);
        }
      }
      function rejected(value) {
        try {
          step(generator['throw'](value));
        } catch (e) {
          reject(e);
        }
      }
      function step(result) {
        result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
      }
      step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
  };

const EditStyleModal = ({ product, visible, onClose, onChange }) => {
  const [form] = Form.useForm();
  const allValues = Form.useWatch([], form);

  const [initialValues, setInitialValues] = useState({});
  const [photo, setPhoto] = useState(null);
  const [msrpModalVisible, setMsrpModalVisible] = useState(false);

  useEffect(() => {
    if (!product) return;

    axiosInstance
      .get(`/products/${product?.id}/photo`)
      .then((response) => {
        setInitialValues({
          style: product?.name,
          status: product?.status,
          description: product?.description,
          photo: response.data.photo ? [{
            uid: "-1",
            name: "photo.png",
            status: "done",
            url: response.data.photo,
          }] : null,
        });

        form.setFieldsValue({
          style: product?.name,
          status: product?.status,
          description: product?.description,
          photo: response.data.photo ? [{
            uid: "-1",
            name: "photo.png",
            status: "done",
            url: response.data.photo,
          }] : null,
        });

        setPhoto(response.data.photo ? [{
          uid: "-1",
          name: "photo.png",
          status: "done",
          url: response.data.photo,
        }] : null);
      })
      .catch((error) => {
        notification.error({
          message: "Error fetching product photo",
          description: error.message,
        });
      });

  }, [product]);

  const saveChanges = () => {
    const currentValues = form.getFieldsValue();
    const formData = new FormData();

    for (const key in currentValues) {
      if (key === "startDate" || key === "endDate") {
        const currentDate = currentValues[key];
        const initialDate = initialValues[key];
        if (
          (currentDate && initialDate && !currentDate.isSame(initialDate)) ||
          (!currentDate && initialDate) ||
          (currentDate && !initialDate)
        ) {
          formData.append(key, currentDate ? currentDate.format("YYYY-MM-DD") : "");
        }
      } else if (key === "photo" && currentValues[key] && currentValues[key].length > 0) {
        formData.append(key, currentValues[key][0].originFileObj);
      } else if (currentValues[key] !== initialValues[key]) {
        formData.append(key, currentValues[key]);
      }
    }

    axiosInstance
      .put(`/products/${product.id}`, formData)
      .then((response) => {
        onChange();
        onClose();
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });
  };

  const handleClose = () => {
    form.setFieldsValue(initialValues);
    onClose();
  };

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      notification.error({
        message: "Error",
        description: "You can only upload JPG/PNG file",
      });
      return Upload.LIST_IGNORE;
    }
    return file;
  };

  const handleChange = ({ fileList: newFileList }) => {
    setPhoto(newFileList);

    if (newFileList.length === 0) {
      form.setFieldValue("photo", null);
      return;
    }
    form.setFieldValue("photo", newFileList);
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  const onPreview = file =>
    __awaiter(void 0, void 0, void 0, function* () {
      let src = file.url;
      if (!src) {
        src = yield new Promise(resolve => {
          const reader = new FileReader();
          reader.readAsDataURL(file.originFileObj);
          reader.onload = () => resolve(reader.result);
        });
      }
      const image = new Image();
      image.src = src;
      const imgWindow = window.open(src);
      imgWindow === null || imgWindow === void 0
        ? void 0
        : imgWindow.document.write(image.outerHTML);
    });

  if (!initialValues) return null;

  return (
    <Modal
      title="Edit Style"
      open={visible}
      onCancel={handleClose}
      onClose={handleClose}
      zIndex={1100}
      footer={
        <>
          <Button
            key="close"
            onClick={handleClose}
          >
            Close
          </Button>
          <Button
            key="save"
            type="primary"
            htmlType="submit"
            onClick={saveChanges}
            disabled={
              allValues?.style === initialValues.style &&
              allValues?.description === initialValues.description &&
              (
                (Array.isArray(allValues?.photo) && Array.isArray(initialValues?.photo) &&
                  allValues.photo.length === initialValues.photo.length &&
                  allValues.photo.every((file, index) => file?.url === initialValues.photo[index]?.url)
                ) ||
                (allValues?.photo == null && initialValues?.photo == null)
              )
            }
          >
            Save
          </Button>
        </>
      }
    >
      <Form
        form={form}
        layout="vertical"
        name="edit-style-form"
        onFinish={saveChanges}
      >
        <Form.Item
          name="style"
          label="Style"
          rules={[{ required: true, message: "Please input the style" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="description"
          label="Description"
          hidden
        >
          <Input.TextArea />
        </Form.Item>
        <Text>MSRP</Text>
        <br />
        <Button
          style={{ marginBottom: 24, marginTop: 8 }}
          onClick={() => setMsrpModalVisible(true)}
        >
          View / Edit MSRP
        </Button>
        <MSRPModal
          visible={msrpModalVisible}
          onClose={() => setMsrpModalVisible(false)}
          onChange={onChange}
          currentMSRP={product?.currentMSRP}
          futureMSRP={product?.futureMSRP}
          style={product?.id}
        />
        <Form.Item
          name="photo"
          label="Photo"
          style={{ marginBottom: 0 }}
        >
          <ImgCrop aspect={1}>
            <Upload
              name="photo"
              listType="picture-card"
              maxCount={1}
              beforeUpload={beforeUpload}
              fileList={photo}
              onChange={handleChange}
              onPreview={onPreview}
              customRequest={({ onSuccess }) => setTimeout(() => onSuccess("ok"), 0)}
            >
              {uploadButton}
            </Upload>
          </ImgCrop>
        </Form.Item>
      </Form>
    </Modal>
  )
};

export default EditStyleModal;